import { usePost } from "../../../api/useCall";
import t from "../../../i18n";
import type useToasts from "@ui/Toast/useToasts";
import validateInterface from "@ui/lib/validateInterface";
import { MeasurePointMetadata } from "./PointMetadata";
import React from "react";
import { DIRECT_INVALID_STATUSES, VALID_STATUSES } from "diagnostics-domain/quantity/metrology";
const getSelectedPoints = (points: Highcharts.Point[]) =>
  points.map(p => validateInterface(MeasurePointMetadata, p.options.custom));

const getQuantityAnalysisIds = (selectedPoints: MeasurePointMetadata[]) =>
  selectedPoints.reduce(
    (
      acc: {
        quantityName: string;
        dates: number[];
      }[],
      { date, quantityName }: { date: number; quantityName: string },
    ) => {
      const existingByQuantityName = acc.find(e => e.quantityName === quantityName);

      if (existingByQuantityName) {
        existingByQuantityName.dates = [...existingByQuantityName.dates, date];
        return acc;
      }

      acc = [...acc, { quantityName, dates: [date] }];
      return acc;
    },
    [],
  );

const useUpdateMetrologyOverrides = (projectId: string) => {
  const overrideMetrology = usePost("/projects/:projectId(uuid)/metrologyOverrides");

  return React.useCallback(
    async ({
      isValid,
      points,
      addToast,
    }: {
      isValid: boolean;
      points: Highcharts.Point[];
      addToast: ReturnType<typeof useToasts>["addToast"];
    }) => {
      const selectedPoints = getSelectedPoints(points);

      const [selectedPointsToCreate, selectedPointsToDelete] = selectedPoints.reduce<
        [MeasurePointMetadata[], MeasurePointMetadata[]]
      >(
        (acc, p) => {
          if (isValid && DIRECT_INVALID_STATUSES.has(p.validity)) {
            acc[1] = [...acc[1], p];
          }

          if (!isValid && VALID_STATUSES.has(p.validity)) {
            acc[0] = [...acc[0], p];
          }
          return acc;
        },
        [[], []],
      );

      const quantityAnalysisDatesToCreate = getQuantityAnalysisIds(selectedPointsToCreate);
      const quantityAnalysisDatesToDelete = getQuantityAnalysisIds(selectedPointsToDelete);

      await overrideMetrology({
        body: {
          validate: quantityAnalysisDatesToDelete.map(m => ({
            ...m,
            dates: m.dates.map(d => new Date(d)),
          })),
          invalidate: quantityAnalysisDatesToCreate.map(m => ({
            ...m,
            dates: m.dates.map(d => new Date(d)),
          })),
        },
        routeParams: { projectId },
      });
      addToast(
        "success",
        isValid
          ? t("measuresSymptoms.toast.analyses_validation", {
              count: selectedPoints.length,
            })
          : t("measuresSymptoms.toast.analyses_invalidation", {
              count: selectedPoints.length,
            }),
      );
    },
    [projectId, overrideMetrology],
  );
};
export default useUpdateMetrologyOverrides;
